import { useClient, useQuery } from '@weenat/client'
import { GetManyAlertsQueryParams } from '@weenat/client/dist/resources/alerts'
import { Alert, GroupedAlerts } from '@weenat/client/dist/resources/alerts.types'
import { useIntl } from '@weenat/wintl'
import useDebounce from 'app/src/hooks/useDebounce'
import { useUserContext } from 'app/userProvider'
import { capitalize } from 'lodash-es'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import { useMemo, useState } from 'react'
import { styled } from 'styled-components'
import EmptyStateWithRipple from '../kit/empty-state/EmptyStateWithRipple'
import InvisibleTextInput from '../kit/fields/InvisibleInput'
import Icons from '../kit/Icons'
import LoadingCircle from '../kit/loaders/LoadingCircle'
import Text from '../kit/Text'
import AlertListItem from './AlertListItem'

const EMPTY_GROUPED_ALERTS: GroupedAlerts = {
  triggered: [],
  activated: [],
  deactivated: []
}

const SearchField = styled(Flex)`
  position: sticky;
  padding: ${(p) => p.theme.spacings.lg}px;
  background: ${(p) => p.theme.colors.grayscale['50']};
  border-bottom: ${(p) => p.theme.borderWidths.sm}px solid ${(p) => p.theme.colors.grayscale['300']};
  top: 0px;
  z-index: 1;
  & input {
    font-size: 16px;
  }
`

const AlertGroupHeader: FC<{
  type: keyof GroupedAlerts
  nbOfAlerts: number
}> = ({ type, nbOfAlerts }) => {
  const { t } = useIntl()

  const label = capitalize(t(`alerts.target_statuses.${type}`))
  const color =
    type === 'triggered' ? 'data.danger' : type == 'activated' ? 'grayscale.black' : 'grayscale.700'

  const Icon =
    type === 'triggered' ? (
      <Icons.NotificationImportant $color={color} $size='md' />
    ) : type === 'activated' ? (
      <Icons.NotificationPaused $color={color} $size='md' />
    ) : (
      <Icons.PauseFilled $color={color} $size='md' />
    )

  return (
    <Flex
      $backgroundColor={'grayscale.100'}
      $p='md'
      $alignItems='center'
      $gap='md'
      $borderColor={'grayscale.200'}
      $borderWidth={0}
      style={{ borderBottomWidth: 1 }}
    >
      <Flex $gap='sm'>
        {Icon}
        <Text $fontSize='rg' $fontWeight='bold' $color={color}>
          {label}
        </Text>
      </Flex>
      <Text $fontSize='sm' $color={'grayscale.700'} $lineHeight='md'>
        {t(nbOfAlerts > 1 ? 'alerts.alerts_count' : 'alerts.singular_alerts_count', { nbOfAlerts })}
      </Text>
    </Flex>
  )
}

const AlertSectionEmptyState = ({ type }: { type: keyof GroupedAlerts }) => {
  const { t } = useIntl()
  return (
    <Flex $alignItems='center' $justifyContent='center' $p='lg'>
      <Text $fontSize='sm' $textAlign='center' $color='grayscale.700'>
        {t(`models.alert.empties.${type}`)}
      </Text>
    </Flex>
  )
}

interface AlertsListProps {
  queryParams?: Partial<GetManyAlertsQueryParams>
  searchable?: boolean
  emptyStateMessage?: string
}

const AlertsList: FC<AlertsListProps> = ({
  queryParams,
  searchable = false,
  emptyStateMessage
}) => {
  const client = useClient()
  const { t } = useIntl()
  const [searchInputValue, setSearchInputValue] = useState('')
  const debouncedSearchInputValue = useDebounce(searchInputValue, 500)
  const { profile } = useUserContext()
  const preferredUnits = !isNil(profile) ? profile.units : undefined

  const requester = useMemo(
    () => client.alerts.getManyGroupedByStatus(preferredUnits, queryParams),
    [client.alerts, preferredUnits, queryParams]
  )

  const request = useQuery(
    {
      ...requester,
      key: [(requester.key[0] as string) + '_groupedByStatus']
    },
    {
      enabled: !isNil(preferredUnits)
    }
  )

  const { data, isLoading } = request
  const alerts = data ?? EMPTY_GROUPED_ALERTS

  const filteredAlerts = useMemo(() => {
    if (!isEmpty(debouncedSearchInputValue)) {
      return {
        triggered: alerts.triggered.filter((alert: Alert) =>
          alert.name.toLowerCase().includes(debouncedSearchInputValue.toLowerCase())
        ),
        activated: alerts.activated.filter((alert: Alert) =>
          alert.name.toLowerCase().includes(debouncedSearchInputValue.toLowerCase())
        ),
        deactivated: alerts.deactivated.filter((alert: Alert) =>
          alert.name.toLowerCase().includes(debouncedSearchInputValue.toLowerCase())
        )
      }
    }

    return alerts
  }, [alerts, debouncedSearchInputValue])

  return (
    <Flex $flex={1} $flexDirection='column' $minHeight={'max-content'}>
      {searchable ? (
        <SearchField>
          <Icons.Search $size='md' />
          <InvisibleTextInput
            shouldFocusOnMount
            id={'alerts'}
            name={'alerts'}
            autoComplete='on'
            autoCorrect='off'
            type='text'
            value={searchInputValue}
            onChange={(e) => {
              if (!isNil(e.currentTarget)) setSearchInputValue(e.currentTarget.value)
            }}
            placeholder={t('models.alert.model.search_item')}
          />
        </SearchField>
      ) : null}

      {isLoading ? (
        <LoadingCircle size='lg' />
      ) : isEmpty(filteredAlerts.triggered) &&
        isEmpty(filteredAlerts.activated) &&
        isEmpty(filteredAlerts.deactivated) ? (
        <Flex $flex={1} $justifyContent='center' $alignItems='center'>
          <EmptyStateWithRipple
            Icon={Icons.Campaign}
            title={emptyStateMessage ?? t('models.alert.model.no_items')}
          />
        </Flex>
      ) : (
        <>
          {Object.entries(filteredAlerts).map(([key, value]) => (
            <>
              <AlertGroupHeader type={key as keyof GroupedAlerts} nbOfAlerts={value.length} />
              {value.length > 0 ? (
                value.map((alert) => <AlertListItem key={alert._id} alert={alert} />)
              ) : (
                <AlertSectionEmptyState type={key as keyof GroupedAlerts} />
              )}
            </>
          ))}
        </>
      )}
    </Flex>
  )
}

export default AlertsList
